import { useEffect, useState } from "react";

import { fetchGeneratedFile } from "../../api";
import { GeneratedFile } from "../../api/models";

const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType.includes("spreadsheet") || mimeType.includes("csv") || mimeType.includes("excel")) return "table_chart";
    if (mimeType.includes("pdf")) return "picture_as_pdf";
    return "description";
};

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Inline preview of a generated image. Fetched with header auth into an object
// URL; renders nothing when the blob is gone (30-day lifecycle rule).
export const GeneratedImage = ({ file }: { file: GeneratedFile }) => {
    const [objectUrl, setObjectUrl] = useState<string>();

    useEffect(() => {
        let cancelled = false;
        let createdUrl: string | undefined;
        fetchGeneratedFile(file.blob_path)
            .then(blob => {
                createdUrl = URL.createObjectURL(blob);
                if (cancelled) {
                    URL.revokeObjectURL(createdUrl);
                    return;
                }
                setObjectUrl(createdUrl);
            })
            .catch(error => {
                console.error("Failed to load generated image:", error);
            });
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [file.blob_path]);

    if (!objectUrl) return null;
    return <img src={objectUrl} alt={file.filename} className="max-w-full rounded-lg border border-jtc-grey" />;
};

const GeneratedFileChip = ({ file }: { file: GeneratedFile }) => {
    const [downloading, setDownloading] = useState(false);
    const [failed, setFailed] = useState(false);

    const onDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        setFailed(false);
        try {
            const blob = await fetchGeneratedFile(file.blob_path, true);
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = file.filename;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download generated file:", error);
            setFailed(true);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            title={failed ? "Download failed — the file may have expired" : `Download ${file.filename}`}
            className="flex items-center gap-2.5 rounded-lg border border-jtc-grey bg-jtc-white px-4 py-2.5 text-left hover:border-jtc-blue transition-colors cursor-pointer"
        >
            <span className="material-symbols-outlined text-[1.5rem] text-jtc-purple">{getFileIcon(file.mime_type)}</span>
            <span className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-jtc-very-dark-grey truncate max-w-[220px]">{file.filename}</span>
                {failed ? (
                    <span className="text-xs text-red-600">Download failed</span>
                ) : (
                    file.size != null && <span className="text-xs text-[#7F7F7F]">{formatFileSize(file.size)}</span>
                )}
            </span>
            <span className="material-symbols-outlined text-[1.25rem] text-jtc-blue shrink-0">
                {downloading ? "hourglass_top" : "download"}
            </span>
        </button>
    );
};

export const GeneratedFileChips = ({ files }: { files: GeneratedFile[] }) => (
    <div className="my-3 flex flex-wrap gap-2">
        {files.map((file, idx) => (
            <GeneratedFileChip key={idx} file={file} />
        ))}
    </div>
);
